import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_FUND, WRITE_FLAGS } from "./_shared.js";

/**
 * `REPORT-EXPORT` (@UC0219) — chat T1: **xuất sổ thu chi của quỹ theo kỳ** (QĐ PO 17/09 #3, `0057`).
 *
 * 🔴 **CSV, KHÔNG phải PDF** — thẻ task viết *"xuất báo cáo PDF"*, nhưng đo được:
 * `ReportsService.createExport` khoá cứng `format: REPORT_FORMAT.CSV` (`reports.service.ts:123`).
 * Worker **biết** làm PDF (`report-export.job.ts:391`) nhưng API **không có đường nào** tạo job PDF ⇒
 * nhánh đó chưa bao giờ chạy. Trang nói **CSV** vì đó là thứ người dùng thật sự nhận được; hứa PDF là
 * nói sai về một việc kiểm chứng được.
 * 🔶 NỢ CÓ ĐỊA CHỈ (không thuộc task trang): API cho chọn `format`, hoặc gỡ nhánh PDF chết ở worker.
 *
 * 🔴 **0 BÚT TOÁN** — trang chỉ tạo một `ReportJob(pending)`; worker sinh file sau. Nên `done` KHÔNG
 * được nói *"đây là file của bạn"*: lúc đó chưa có file nào. Câu phải nói **đang làm** và **lấy ở đâu**.
 *
 * Vai = O/A/T, copy ĐÚNG `@RequireFundRoles` của cửa GUI (`reports.controller.ts:67`) — mời rộng hơn
 * cửa là dẫn người ta tới 403 ở bước chốt (CLAUDE.md §4).
 */
export const REPORT_EXPORT = {
  id: "REPORT-EXPORT",
  uc: "@UC0219",
  tier: "T1",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_FUND,
    /**
     * Kỳ báo cáo — TUỲ CHỌN ở cả hai đầu. Cửa GUI nhận `from`/`to` optional và **vắng ⇒ toàn bộ lịch
     * sử**, nên bắt buộc ở đây là chặt hơn cửa mà không có lý do.
     */
    /*
     * 🔴 US-AIV2-0114 L07 (@UC0219): hai ô là HAI ĐẦU của một khoảng (`rangeEdge`) ⇒ bộ đọc ngày hiểu cụm khoảng ("tháng này" · "cả năm" ·
     * "tháng trước" · "quý này" · "năm ngoái" · "tháng 9") và lưới khoảng đưa chữ của câu cho cả hai đầu khi Phiếu không gạch ô ngày nào
     * (đo 01/10: 12/12 lượt A12 ra "(toàn bộ lịch sử)"). Không vào prompt ⇒ bản ghi eval không mục.
     */
    { id: "tuNgay", type: "date", required: false, ask: "Từ ngày nào? (bỏ trống = từ đầu)", rangeEdge: "from" },
    { id: "denNgay", type: "date", required: false, ask: "Đến ngày nào? (bỏ trống = tới hôm nay)", rangeEdge: "to" },
  ],
  llm: {
    description:
      "SINH RA MỘT FILE sổ thu chi của quỹ theo khoảng thời gian, để tải về máy hoặc gửi cho nhóm — 'xuất file', 'tải excel/csv/pdf', 'export', 'download', 'in sổ', 'cho mình file' đều là việc này. Chỉ muốn MỞ MÀN HÌNH xem báo cáo bằng mắt, không cần file, thì là REPORT-VIEW.",
    examples: [
      "xuất sổ thu chi quỹ lớp",
      "xuat so thu chi quy lop",
      "tai bao cao quy thang nay",
      "cho mình file thu chi từ đầu năm",
      "cho minh file thu chi tu dau nam",
      "xuất excel quỹ",
      "export sổ quỹ",
      "in sổ thu chi",
      "xuat bao cao",
      "tải csv thu chi",
      // ——— 0105: thêm cho đủ 40 ———
      "tai csv thu chi",
      "xuat excel quy",
      "export so quy",
      "in so thu chi",
      "xuất file sổ quỹ quý này",
      "xuat file so quy quy nay",
      "cho mình bản kê thu chi của quỹ",
      "cho minh ban ke thu chi cua quy",
      "tải file thu chi quỹ từ tháng 1 đến tháng 6",
      "tai file thu chi quy tu thang 1 den thang 6",
      "xuất sổ quỹ cả năm",
      "xuat so quy ca nam",
      "kết xuất dữ liệu thu chi",
      "ket xuat du lieu thu chi",
      "tạo file sổ quỹ để gửi nhóm",
      "tao file so quy de gui nhom",
      "lấy file thu chi quỹ về máy",
      "lay file thu chi quy ve may",
      "xuất sổ quỹ từ đầu tháng tới giờ",
      "xuat so quy tu dau thang toi gio",
      "mình cần file thu chi để họp phụ huynh",
      "minh can file thu chi de hop phu huynh",
      "download sổ thu chi",
      "download so thu chi",
      "xuất dữ liệu quỹ ra file",
      "xuat du lieu quy ra file",
      "làm cho mình file sổ quỹ tháng trước",
      "lam cho minh file so quy thang truoc",
      "xuất sổ thu chi theo kỳ",
      "xuat so thu chi theo ky",
    ],
  },
  executor: "reports.createExport",
  /**
   * `@UC0216` — **RỖNG có chủ đích**, và lần này nó là một QUYẾT ĐỊNH ĐÃ GHI, không phải chỗ trống.
   *
   * `apps/mobile` **cố ý KHÔNG** có `POST /funds/:id/reports/export` —
   * `reports_api_client.dart:10-19` ghi nguyên văn: *"việc xuất báo cáo [cấp quỹ] đi qua biểu mẫu
   * `RPT-EXPORT` của trợ lý, và dựng thêm một lối tạo thứ hai ở client là hai cơ chế cho cùng một
   * việc"* (QĐ người dùng 10/09/2026, US-MOB.F12-0760). Cửa cấp NHÓM thì mobile có, và nó thuộc trang
   * `REPORT-VIEW`.
   *
   * ⇒ Trang này là **đường DUY NHẤT** tới cửa xuất cấp quỹ. `endpoints` khai *"cửa **của mobile** mà
   * trang chịu trách nhiệm"*, nên một cửa mobile **cố ý không gọi** không thuộc bản đồ đó.
   * 🔁 Nếu ngày nào mobile dựng nút xuất cấp quỹ: khai vào đây, và **dùng chung** cửa — điều bị cấm là
   * một **engine xuất** thứ hai, không phải thêm một nút.
   */
  endpoints: [],
  facts: ["tenQuy", "ky", "ketQua"],
  templates: {
    confirm: "Xuất sổ thu chi của quỹ {tenQuy} ({ky})?",
    done: "Đang làm file sổ thu chi của {tenQuy} ({ky}). {ketQua}",
  },
  labels: {
    tenQuy: "Quỹ",
    ky: "Kỳ",
    /**
     * 🔴 Câu này phải nói **chưa có file** và **lấy ở đâu**. Cửa trả `202 pending`; worker làm sau.
     * Khai "đã xuất xong" là nói sai về một việc chưa xảy ra.
     */
    ketQua: "Tiếp theo",
  },
  journey: { outcomes: { done: { next: ["READ-FUND-MONEY"] } } },
  help: { title: "Xuất báo cáo thu chi", tryNow: "xuất sổ thu chi quỹ lớp" },
} satisfies AiCapabilityPage;
